import { Zap } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { TddSummary } from '../types'

interface TddSummaryCardProps {
  tddSummary: TddSummary
  /** Points earned for each DD matchday (indexed same as matchdays array) */
  ddPoints?: { apertura: number[]; clausura: number[] }
}

function DdPip({ used, matchday, points }: { used: boolean; matchday?: number; points?: number }) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold tabular-nums transition-colors
                      ${used
                        ? 'bg-violet-500 dark:bg-violet-400 text-white dark:text-zinc-900'
                        : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-400 dark:text-zinc-500'}`}
        >
          {used ? matchday : '–'}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {used ? (
          <div className="flex flex-col items-center gap-0.5">
            <span className="font-medium">Matchday {matchday}</span>
            {points !== undefined && (
              <span className={points >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                {points >= 0 ? '+' : ''}{points} pts
              </span>
            )}
          </div>
        ) : (
          <span className="text-zinc-500 dark:text-zinc-400">DD available</span>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

function HalfStatus({ label, used, total, matchdays, points }: {
  label: string
  used: number
  total: number
  matchdays: number[]
  points?: number[]
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
          {label}
        </span>
        <div className="flex items-center gap-1.5">
          {Array.from({ length: total }, (_, i) => (
            <DdPip key={i} used={i < used} matchday={matchdays[i]} points={points?.[i]} />
          ))}
        </div>
      </div>
      <span className="text-sm font-medium tabular-nums text-zinc-900 dark:text-zinc-100">
        {used}/{total}
      </span>
    </div>
  )
}

export function TddSummaryCard({ tddSummary, ddPoints }: TddSummaryCardProps) {
  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800
                    bg-white dark:bg-zinc-900 p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-6 h-6 rounded-md
                        bg-violet-100 dark:bg-violet-900/40">
          <Zap className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
        </div>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Double Down
        </h3>
      </div>
      <div className="space-y-2.5">
        <HalfStatus
          label="Apertura"
          used={tddSummary.apertura.used}
          total={tddSummary.apertura.total}
          matchdays={tddSummary.apertura.matchdays}
          points={ddPoints?.apertura}
        />
        <HalfStatus
          label="Clausura"
          used={tddSummary.clausura.used}
          total={tddSummary.clausura.total}
          matchdays={tddSummary.clausura.matchdays}
          points={ddPoints?.clausura}
        />
      </div>
    </div>
  )
}
